import React from "react";
import { Outlet, useParams } from "react-router";
import { Link, Page, PageNotFound, VerticalDivider } from "@sscs/ui";
import { AddToCatalog } from "Components";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useAllowedTo, useProjectsRoute } from "Hooks";
import { isApproved } from "Utils";
import LikeDislike from "../../components/LikeDislike/LikeDislike";
import useCatalogs from "../../hooks/useCatalogs";
import { Catalog, CatalogNode } from "../../types";
import { getCountDetails } from "../../utils";

type Crumb = Pick<CatalogNode, "label"> & { route: string | null };

const ContentLayout = (): React.JSX.Element => {
  const { groupId, catalogId, packageId } = useParams();
  const { catalogs, status, selectedCatalog, catalogPackage, catalogVersion } =
    useCatalogs();
  const canCurateCatalog = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const projectsRoute = useProjectsRoute(
    catalogPackage?.project_id,
    catalogPackage?.package_id,
  );

  if (!catalogs) {
    return (
      <Page.Root>
        <Page.Body status={status} />
      </Page.Root>
    );
  }

  const catalog: Catalog | undefined = catalogId
    ? catalogs[catalogId]
    : undefined;

  if (!catalog) {
    return (
      <PageNotFound
        title="We can't find that catalog"
        byline="Try selecting a catalog from the sidebar"
      />
    );
  }

  const current = selectedCatalog?.path.at(-1);
  const crumbs: Crumb[] = [
    { label: catalog.title, route: null },
    ...(selectedCatalog
      ? [
          ...selectedCatalog.path,
          ...(packageId
            ? [{ label: catalogPackage?.name ?? "", route: null }]
            : []),
        ]
      : []),
  ];

  const actions: React.JSX.Element[] = [];
  if (catalog.catalogId === ORGANIZATION_CATALOG_ID && catalogPackage) {
    if (canCurateCatalog) {
      actions.push(
        <Link key="manage" fullWidth={false} to={projectsRoute}>
          Manage versions
        </Link>,
      );
      if (catalogVersion) {
        actions.push(
          <AddToCatalog
            key="add"
            packageId={catalogPackage.package_id}
            packageName={catalogPackage.name}
            inCatalog={catalogPackage.in_catalog}
            disabled={!isApproved(catalogVersion.approval_status)}
          />,
        );
      }
    }
    if (catalogPackage.in_catalog) {
      actions.push(
        <LikeDislike
          key="like"
          packageId={catalogPackage.package_id}
          userLike={catalogPackage.user_like}
          popularity={null}
        />,
      );
    }
  }

  return (
    <Page.Root>
      <Page.Header
        title={crumbs.map((crumb, index) => (
          <React.Fragment key={crumb.label}>
            {index > 0 && <span>&nbsp;/&nbsp;</span>}
            {index < crumbs.length - 1 && crumb.route ? (
              <Link inline underline="always" to={crumb.route}>
                {crumb.label}
              </Link>
            ) : (
              <span>{crumb.label}</span>
            )}
          </React.Fragment>
        ))}
        details={
          !packageId && current
            ? getCountDetails(
                canCurateCatalog && current.total !== null
                  ? [current.count, current.total]
                  : [current.count],
              )
            : undefined
        }
        actions={actions.map((action, index) => (
          <React.Fragment key={action.key}>
            {index > 0 && <VerticalDivider />}
            {action}
          </React.Fragment>
        ))}
      />
      {selectedCatalog ? (
        <Outlet />
      ) : (
        <PageNotFound
          title="We can't find that platform"
          byline="Try selecting a platform from the sidebar"
        />
      )}
    </Page.Root>
  );
};

export default ContentLayout;
